/**
 * ============================================================
 * KEJJA OS — src/hooks/useNotificationSound.ts
 * Synthesizes a pleasant two-tone chime via Web Audio API.
 * No bundled audio assets.
 * ============================================================
 */

import { useCallback, useRef } from 'react';

type ChimeVariant = 'normal' | 'critical' | 'quiet';

const VARIANTS: Record<ChimeVariant, {
  frequencies: number[];
  durationMs: number;
  volume: number;
}> = {
  normal:   { frequencies: [880, 1175],        durationMs: 400, volume: 0.15 },  // A5 → D6 (perfect fourth up)
  critical: { frequencies: [1320, 990, 1320],  durationMs: 550, volume: 0.22 },  // E6-B5-E6 alert pattern
  quiet:    { frequencies: [660],              durationMs: 250, volume: 0.08 },  // single E5 soft
};

export function useNotificationSound() {
  const ctxRef = useRef<AudioContext | null>(null);

  const getCtx = (): AudioContext | null => {
    if (ctxRef.current) return ctxRef.current;
    try {
      const AC = window.AudioContext ?? (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      ctxRef.current = new AC();
      return ctxRef.current;
    } catch {
      return null;
    }
  };

  const playChime = useCallback((variant: ChimeVariant = 'normal') => {
    const ctx = getCtx();
    if (!ctx) return;

    // Resume if suspended (browsers pause audio context until user interaction)
    if (ctx.state === 'suspended') {
      ctx.resume().catch(() => { /* ignore */ });
    }

    const cfg = VARIANTS[variant];
    const now = ctx.currentTime;

    cfg.frequencies.forEach((freq, i) => {
      const startOffset = (cfg.durationMs / cfg.frequencies.length) * i / 1000;
      const noteDuration = (cfg.durationMs / cfg.frequencies.length) / 1000 * 1.3;

      const oscillator = ctx.createOscillator();
      const gain = ctx.createGain();

      oscillator.type = 'sine';
      oscillator.frequency.setValueAtTime(freq, now + startOffset);

      // Envelope: quick attack, gentle decay
      gain.gain.setValueAtTime(0, now + startOffset);
      gain.gain.linearRampToValueAtTime(cfg.volume, now + startOffset + 0.015);
      gain.gain.exponentialRampToValueAtTime(0.001, now + startOffset + noteDuration);

      oscillator.connect(gain);
      gain.connect(ctx.destination);

      oscillator.start(now + startOffset);
      oscillator.stop(now + startOffset + noteDuration + 0.05);
    });
  }, []);

  return { playChime };
}
